import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import { useCopilotContext } from "util/copilotContext";
import { useCopilotShell } from "contexts/CopilotShellContext";
import {
  ADVISOR_POPUP_RUN_EVENT,
  findDashboardByName,
  takeQueuedAdvisorPopupPrompt,
} from "util/advisorPopup";
import {
  CloseOutlined,
  ExpandOutlined,
  SendOutlined,
  ClearOutlined,
  PlusOutlined,
  HistoryOutlined,
  DownOutlined,
  UpOutlined,
  FullscreenOutlined,
  ColumnWidthOutlined,
  BarChartOutlined,
  FileTextOutlined,
  BulbOutlined,
  EllipsisOutlined,
  SearchOutlined,
  DownloadOutlined,
  LikeOutlined,
  LikeFilled,
  DislikeOutlined,
  DislikeFilled,
  CopyOutlined,
  AudioOutlined,
  AudioMutedOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Input, message, Spin, Tag } from "antd";
import { getIntlLocale, normalizeAppLanguage } from "util/locale";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styles from "./style.module.scss";
import AdvisorChartBlock from "./AdvisorChartBlock";
import AdvisorRatingCard from "./AdvisorRatingCard";
import useAdvisorRatingCard from "./useAdvisorRatingCard";
import {
  streamCxGptChat,
  normalizeThoughtPreview,
  formatGptResponseHtml,
  fetchCxGptCatalog,
  fetchCxGptSettings,
  fetchCxGptModels,
  fetchOrgIndustry,
} from "util/copilotChatApi";
import {
  resolveSectorExperienceLabel,
} from "util/advisorSectorLabel";
import {
  ADVISOR_MAX_CONTEXT_TOKENS,
  estimateAdvisorContextUsage,
} from "util/copilotContextUsage";
import {
  createCxGptSession,
  fetchCxGptSession,
  fetchCxGptSessions,
  importCxGptSessions,
  mapApiSessionDetail,
  mapApiSessionSummary,
  mapClientSessionForImport,
  mapClientSessionForPut,
  renameCxGptSession,
  submitCxGptMessageFeedback,
  updateCxGptSession,
} from "util/copilotSessionApi";
import { trackAdvisorSessionStarted, trackAdvisorMessageSent } from "util/productAnalytics";
import { pinNavbarIntoView } from "helperComponents/AppShellScrollLock";
import {
  deriveSessionAnalyticsScope,
  analyticsScopeForApi,
  chipPeriodWindow,
  lookbackDateWindow,
  resolveContextDashboardId,
  deriveAnalyticsScopeFromMessages,
  lastDashboardSelectionFromMessages,
  mergeSessionAnalyticsScope,
} from "util/advisorAnalyticsScope";
import ContextUsageIndicator from "./ContextUsageIndicator";
import AdvisorIcon from "./AdvisorIcon";
import ChatSessionRail from "./ChatSessionRail";
import ExportChatModal from "./ExportChatModal";
import {
  ADVISOR_VOICE_CONNECTION,
  useAdvisorVoice,
} from "hooks/useAdvisorVoice";

const COPILOT_STORAGE_KEY = "pivony_copilot_v2";
const COPILOT_DB_MIGRATED_KEY = "pivony_copilot_db_migrated_v1";
const PAGE_RAIL_COLLAPSED_KEY = "pivony_advisor_page_rail_collapsed";
const LEGACY_SESSION_KEY = "pivony_copilot_session";
const SUGGESTIONS_DISMISSED_KEY = "pivony_copilot_suggestions_dismissed";
const MAX_SESSIONS = 25;

const newId = () =>
  `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const stripHtmlToText = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof document === "undefined") return html.replace(/<[^>]+>/g, " ").trim();
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

const defaultState = () => {
  const id = newId();
  return {
    sessions: [
      {
        id,
        title: "New chat",
        titleIsCustom: false,
        messages: [],
        updatedAt: Date.now(),
        contextUsedTokens: 0,
        contextMaxTokens: ADVISOR_MAX_CONTEXT_TOKENS,
        messagesLoaded: true,
        analyticsScope: null,
      },
    ],
    activeSessionId: id,
    lastRoute: null,
  };
};

const normalizeState = (raw) => {
  if (!raw || !Array.isArray(raw.sessions) || raw.sessions.length === 0) {
    return defaultState();
  }
  const sessions = raw.sessions.map((s) => ({
    id: s.id || newId(),
    title: s.title || "Chat",
    titleIsCustom: s.titleIsCustom === true,
    messages: Array.isArray(s.messages) ? s.messages : [],
    updatedAt: typeof s.updatedAt === "number" ? s.updatedAt : Date.now(),
    contextUsedTokens:
      typeof s.contextUsedTokens === "number"
        ? s.contextUsedTokens
        : 0,
    contextMaxTokens:
      typeof s.contextMaxTokens === "number"
        ? s.contextMaxTokens
        : ADVISOR_MAX_CONTEXT_TOKENS,
    messagesLoaded: s.messagesLoaded !== false,
    analyticsScope:
      s.analyticsScope && typeof s.analyticsScope === "object"
        ? s.analyticsScope
        : null,
  }));
  let activeSessionId = raw.activeSessionId;
  if (!sessions.some((s) => s.id === activeSessionId)) {
    activeSessionId = sessions[0].id;
  }
  return {
    sessions,
    activeSessionId,
    lastRoute: raw.lastRoute || null,
  };
};

const loadState = () => {
  try {
    const stored = localStorage.getItem(COPILOT_STORAGE_KEY);
    if (stored) {
      return normalizeState(JSON.parse(stored));
    }
  } catch (_) {}

  try {
    const leg = localStorage.getItem(LEGACY_SESSION_KEY);
    if (leg) {
      const p = JSON.parse(leg);
      localStorage.removeItem(LEGACY_SESSION_KEY);
      if (p && Array.isArray(p.messages) && p.messages.length) {
        const id = newId();
        return normalizeState({
          sessions: [
            {
              id,
              title: (p.lastRoute || "Imported").slice(0, 56),
              messages: p.messages.map((m) => ({
                ...m,
                ts: m.ts || Date.now(),
              })),
              updatedAt: Date.now(),
            },
          ],
          activeSessionId: id,
          lastRoute: p.lastRoute || null,
        });
      }
    }
  } catch (_) {}

  return defaultState();
};

const saveStateToStorage = (state) => {
  try {
    localStorage.setItem(COPILOT_STORAGE_KEY, JSON.stringify(state));
  } catch (_) {}
};

const loadSuggestionsDismissed = () => {
  try {
    return localStorage.getItem(SUGGESTIONS_DISMISSED_KEY) === "1";
  } catch (_) {
    return false;
  }
};

const saveSuggestionsDismissed = (dismissed) => {
  try {
    if (dismissed) {
      localStorage.setItem(SUGGESTIONS_DISMISSED_KEY, "1");
    } else {
      localStorage.removeItem(SUGGESTIONS_DISMISSED_KEY);
    }
  } catch (_) {}
};

const savePageRailCollapsed = (collapsed) => {
  try {
    localStorage.setItem(PAGE_RAIL_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch (_) {}
};

/** Legacy sessions stored assistant HTML; strip for LLM API payload. */
const assistantContentForApi = (msg) => {
  if (msg.role !== "assistant") return msg.content;
  if (msg.isHTML && typeof msg.content === "string") {
    try {
      const tmp = document.createElement("div");
      tmp.innerHTML = msg.content;
      return (tmp.textContent || tmp.innerText || "").trim();
    } catch (_) {
      return msg.content.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
  }
  return msg.content;
};

const buildApiMessages = (msgs) =>
  msgs
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      role: m.role,
      content: assistantContentForApi(m),
    }));

const patchActiveSession = (prev, updater) => {
  const { activeSessionId } = prev;
  const sessions = prev.sessions.map((s) => {
    if (s.id !== activeSessionId) return s;
    return updater(s);
  });
  return { ...prev, sessions };
};

/** DEBUG: normalize API `error` field (string or object) for display. */
const cxGptApiErrorText = (result) => {
  if (!result || result.error == null) return "";
  const raw = result.error;
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object") {
    if (typeof raw.message === "string" && raw.message.trim()) {
      return raw.message.trim();
    }
    try {
      return JSON.stringify(raw);
    } catch (_) {
      return "";
    }
  }
  return String(raw).trim();
};

const withMessageGroups = (messages) =>
  messages.map((msg, index) => {
    const prev = messages[index - 1];
    return {
      ...msg,
      isFirstInGroup: !prev || prev.role !== msg.role,
    };
  });

const deriveNameFromEmail = (email) => {
  if (typeof email !== "string" || !email.includes("@")) {
    return "";
  }
  const local = email.split("@")[0]?.trim().toLowerCase();
  if (!local || local.length < 2) {
    return "";
  }
  const blocked = new Set([
    "info",
    "admin",
    "contact",
    "support",
    "hello",
    "test",
    "noreply",
    "no-reply",
  ]);
  const segment = local.split(/[._+-]/)[0];
  if (!segment || blocked.has(segment)) {
    return "";
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1);
};

const resolveAdvisorFirstName = (reduxUser, accountUser) => {
  const candidates = [
    reduxUser?.firstName,
    accountUser?.firstName,
    reduxUser?.displayName,
    accountUser?.displayName,
    typeof reduxUser?.fullName === "string"
      ? reduxUser.fullName.trim().split(/\s+/)[0]
      : "",
    typeof accountUser?.fullName === "string"
      ? accountUser.fullName.trim().split(/\s+/)[0]
      : "",
    typeof reduxUser?.name === "string"
      ? reduxUser.name.trim().split(/\s+/)[0]
      : "",
    typeof accountUser?.name === "string"
      ? accountUser.name.trim().split(/\s+/)[0]
      : "",
    deriveNameFromEmail(accountUser?.email),
    deriveNameFromEmail(reduxUser?.email),
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
};

const resolveUserInitial = (firstName) => {
  const trimmed = typeof firstName === "string" ? firstName.trim() : "";
  if (!trimmed) return "?";
  return trimmed.charAt(0).toLocaleUpperCase("tr-TR");
};

const DASHBOARD_PICKER_SMART_DEFAULTS = 3;

/**
 * In-chat dashboard chooser the advisor surfaces when a question doesn't yet
 * name a dashboard. Shows the current page's dashboard first, then a few smart
 * defaults, with typeahead search and a client-side "show more" for the rest.
 */
const DashboardPicker = ({ picker, disabled, onPick, t }) => {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const dashboards = Array.isArray(picker?.dashboards) ? picker.dashboards : [];
  const groups = Array.isArray(picker?.groups) ? picker.groups : [];
  const defaultId =
    picker?.default_dashboard_id != null ? picker.default_dashboard_id : null;
  const hasGroups = groups.length > 0;

  const ordered = useMemo(() => {
    const list = dashboards.filter((d) => d && d.id != null);
    if (defaultId != null) {
      list.sort((a, b) => {
        if (a.id === defaultId) return -1;
        if (b.id === defaultId) return 1;
        return 0;
      });
    }
    return list;
  }, [dashboards, defaultId]);

  const groupCounts = useMemo(() => {
    const counts = new Map();
    ordered.forEach((d) => {
      const key = d.group_id != null ? d.group_id : "ungrouped";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [ordered]);

  const visibleGroups = useMemo(() => {
    return groups.filter((g) => g && g.id != null && groupCounts.get(g.id) > 0);
  }, [groups, groupCounts]);

  useEffect(() => {
    if (!hasGroups) {
      setSelectedGroupId(null);
      return;
    }
    if (selectedGroupId != null) return;
    if (visibleGroups.length === 1) {
      setSelectedGroupId(visibleGroups[0].id);
      return;
    }
    const defaultDash = ordered.find((d) => d.id === defaultId);
    if (defaultDash?.group_id != null) {
      setSelectedGroupId(defaultDash.group_id);
    }
  }, [hasGroups, visibleGroups, ordered, defaultId, selectedGroupId]);

  const q = query.trim().toLowerCase();
  const scoped = useMemo(() => {
    if (q) return ordered;
    if (!hasGroups || selectedGroupId == null) return [];
    if (selectedGroupId === "all") return ordered;
    if (selectedGroupId === "ungrouped") {
      return ordered.filter((d) => d.group_id == null);
    }
    return ordered.filter((d) => d.group_id === selectedGroupId);
  }, [ordered, q, hasGroups, selectedGroupId]);

  const filtered = q
    ? scoped.filter((d) => String(d.name || "").toLowerCase().includes(q))
    : scoped;

  const showAll = Boolean(q) || expanded || !hasGroups;
  const visible = showAll
    ? filtered
    : filtered.slice(0, DASHBOARD_PICKER_SMART_DEFAULTS);
  const remaining = filtered.length - visible.length;

  if (!ordered.length) return null;

  const showPickGroupHint =
    hasGroups && selectedGroupId == null && !q && visible.length === 0;

  return (
    <div className={styles.dashboardPicker}>
      {hasGroups ? (
        <div className={styles.dashboardPickerGroups} role="listbox" aria-label={t("cx_gpt.dashboard_picker_groups")}>
          <button
            type="button"
            className={`${styles.dashboardPickerGroupChip} ${
              selectedGroupId === "all" ? styles.dashboardPickerGroupChipActive : ""
            }`.trim()}
            onClick={() => setSelectedGroupId("all")}
            disabled={disabled}
          >
            {t("cx_gpt.dashboard_picker_all_groups")}
          </button>
          {visibleGroups.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`${styles.dashboardPickerGroupChip} ${
                selectedGroupId === g.id ? styles.dashboardPickerGroupChipActive : ""
              }`.trim()}
              onClick={() => setSelectedGroupId(g.id)}
              disabled={disabled}
            >
              {g.name}
            </button>
          ))}
          {groupCounts.get("ungrouped") > 0 ? (
            <button
              type="button"
              className={`${styles.dashboardPickerGroupChip} ${
                selectedGroupId === "ungrouped"
                  ? styles.dashboardPickerGroupChipActive
                  : ""
              }`.trim()}
              onClick={() => setSelectedGroupId("ungrouped")}
              disabled={disabled}
            >
              {t("cx_gpt.dashboard_picker_ungrouped")}
            </button>
          ) : null}
        </div>
      ) : null}
      <Input
        size="small"
        allowClear
        prefix={<SearchOutlined />}
        placeholder={t("cx_gpt.dashboard_picker_search")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        className={styles.dashboardPickerSearch}
      />
      {showPickGroupHint ? (
        <div className={styles.dashboardPickerEmpty}>
          {t("cx_gpt.dashboard_picker_pick_group")}
        </div>
      ) : null}
      <div className={styles.dashboardPickerList}>
        {visible.map((d) => (
          <button
            key={d.id}
            type="button"
            className={`${styles.dashboardPickerItem} ${
              d.id === defaultId ? styles.dashboardPickerItemCurrent : ""
            }`.trim()}
            onClick={() => onPick(d)}
            disabled={disabled}
          >
            <span className={styles.dashboardPickerItemName}>{d.name}</span>
            {d.id === defaultId ? (
              <span className={styles.dashboardPickerBadge}>
                {t("cx_gpt.dashboard_picker_current")}
              </span>
            ) : null}
          </button>
        ))}
        {!showPickGroupHint && visible.length === 0 ? (
          <div className={styles.dashboardPickerEmpty}>
            {t("cx_gpt.dashboard_picker_empty")}
          </div>
        ) : null}
      </div>
      {!showAll && remaining > 0 ? (
        <button
          type="button"
          className={styles.dashboardPickerMore}
          onClick={() => setExpanded(true)}
          disabled={disabled}
        >
          {t("cx_gpt.dashboard_picker_more", { count: remaining })}
        </button>
      ) : null}
    </div>
  );
};

const PERIOD_PICKER_LABEL_KEYS = {
  7: "period_picker_7",
  30: "period_picker_30",
  90: "period_picker_90",
};

const PeriodPicker = ({ picker, disabled, onPick, t }) => {
  const periods = Array.isArray(picker?.periods)
    ? picker.periods.filter((p) => p && Number(p.days) > 0)
    : [];
  if (!periods.length) return null;
  return (
    <div
      className={styles.periodPicker}
      role="listbox"
      aria-label={t("cx_gpt.period_picker_label")}
    >
      {periods.map((p) => {
        const days = Number(p.days);
        const labelKey = PERIOD_PICKER_LABEL_KEYS[days];
        return (
          <button
            key={days}
            type="button"
            className={styles.periodPickerChip}
            onClick={() => onPick(days)}
            disabled={disabled}
          >
            {labelKey
              ? t(`cx_gpt.${labelKey}`)
              : t("cx_gpt.period_picker_n", { days })}
          </button>
        );
      })}
    </div>
  );
};

const KpiTeamPicker = ({ picker, disabled, onPick, t }) => {
  const teams = Array.isArray(picker?.teams) ? picker.teams : [];
  if (!teams.length) return null;
  return (
    <div
      className={styles.kpiTeamPicker}
      role="listbox"
      aria-label={t("cx_gpt.kpi_team_picker_label")}
    >
      {teams.map((team) => {
        const label = team.name || team.team_id;
        return (
          <button
            key={String(team.team_id)}
            type="button"
            className={styles.kpiPickerChip}
            onClick={() => onPick(team)}
            disabled={disabled}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

const KpiMetricPicker = ({ picker, disabled, onPick, t }) => {
  const metrics = Array.isArray(picker?.metrics) ? picker.metrics : [];
  if (!metrics.length) return null;
  return (
    <div
      className={styles.kpiMetricPicker}
      role="listbox"
      aria-label={t("cx_gpt.kpi_metric_picker_label")}
    >
      <span className={styles.kpiMetricPickerHint}>
        {picker.dashboard_name
          ? t("cx_gpt.kpi_metric_picker_for", { dashboard: picker.dashboard_name })
          : t("cx_gpt.kpi_metric_picker_label")}
      </span>
      <div className={styles.kpiMetricPickerList}>
        {metrics.map((metric) => (
          <button
            key={metric.label}
            type="button"
            className={styles.kpiPickerChip}
            onClick={() => onPick(metric.label)}
            disabled={disabled}
          >
            {metric.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const GlobalCopilotPanel = ({
  onClose,
  theme = "dark",
  onToggleWide,
  variant = "panel",
  onOpenFullPage,
  onDockToPanel,
  accountUser,
  sparklineOnly = false,
  historyAsDrawer = false,
}) => {
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.userReducer);
  const { setCopilotOpen, hasCxGptAccess, advisorPlanName, advisorIsPro, advisorTrial } =
    useCopilotShell();
  const trialEnded = advisorTrial?.active === false;
  const context = useCopilotContext();
  const [copilotState, setCopilotState] = useState(defaultState);
  const [sessionsSource, setSessionsSource] = useState("local");
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsReady, setSessionsReady] = useState(false);
  const [popupRunTick, setPopupRunTick] = useState(0);
  const [popupPromptToSend, setPopupPromptToSend] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingThought, setStreamingThought] = useState("");
  const [streamingToolStatus, setStreamingToolStatus] = useState("");
  const [streamContentStarted, setStreamContentStarted] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [pageRailCollapsed, setPageRailCollapsed] = useState(false);
  const [panelHistoryOpen, setPanelHistoryOpen] = useState(false);
  const [exportChatOpen, setExportChatOpen] = useState(false);
  const [modelsPayload, setModelsPayload] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [catalogPayload, setCatalogPayload] = useState([]);
  const [llmSource, setLlmSource] = useState("pivony_local");
  const [selectedChatModel, setSelectedChatModel] = useState(null);
  const [activeGptModelId, setActiveGptModelId] = useState(null);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(
    loadSuggestionsDismissed
  );
  const [contextExpanded, setContextExpanded] = useState(false);
  const [showCapabilities, setShowCapabilities] = useState(false);
  const [industryName, setIndustryName] = useState(null);
  const messagesEndRef = useRef(null);
  const messageListRef = useRef(null);
  const persistTimerRef = useRef(null);
  const activeStreamGenRef = useRef(0);
  const sendInFlightRef = useRef(false);
  const copilotStateRef = useRef(copilotState);
  copilotStateRef.current = copilotState;

  const formatSessionMeta = useCallback(
    (ts, groupKey) => {
      if (!ts) return "";
      try {
        const locale = getIntlLocale(i18n.language);
        const timeOpts = {
          hour: "2-digit",
          minute: "2-digit",
          ...(locale === "tr-TR" ? { hour12: false } : {}),
        };

        if (groupKey === "today" || groupKey === "yesterday") {
          return new Date(ts).toLocaleTimeString(locale, timeOpts);
        }

        return new Date(ts).toLocaleDateString(locale, {
          day: "numeric",
          month: "short",
        });
      } catch (_) {
        return "";
      }
    },
    [i18n.language]
  );

  const translateRangeLabel = useCallback(
    (raw) => {
      if (raw == null || raw === "") return "";
      const s = String(raw);
      const tryOne = (suffix) => {
        const k = `cx_gpt.range_${suffix}`;
        const v = t(k);
        return v !== k ? v : null;
      };
      return tryOne(s) || tryOne(s.toLowerCase()) || s;
    },
    [t]
  );

  // The chip must mirror the window the advisor actually queries. When the URL
  // carries an explicit since/until (the canonical filter), show that exact
  // range; only fall back to the preset label when since/until is absent.
  const formatContextRange = useCallback(
    (since, until, fallbackRange) => {
      if (since && until) {
        const locale = getIntlLocale(i18n.language);
        const fmt = (d) => {
          const dt = new Date(d);
          if (isNaN(dt.getTime())) return d;
          return dt.toLocaleDateString(locale, { day: "numeric", month: "short" });
        };
        return `${fmt(since)} – ${fmt(until)}`;
      }
      return translateRangeLabel(fallbackRange);
    },
    [i18n.language, translateRangeLabel]
  );

  const displaySessionTitle = useCallback(
    (title) => {
      if (title === "New chat") return t("cx_gpt.new_chat");
      if (title === "Chat") return t("cx_gpt.session_title_chat");
      if (title === "Imported") return t("cx_gpt.session_title_imported");
      return title;
    },
    [t]
  );

  const resolveChatModelForApi = useCallback(() => {
    // UI artık sadece "Ask mode" gösteriyor; model/preset seçimi yok.
    // Backend için "auto" mantığı: fetchCxGptModels tarafından gelen selectedChatModel kullanılır.
    return selectedChatModel || undefined;
  }, [selectedChatModel]);

  const loadCxGptSettings = useCallback(async () => {
    const s = await fetchCxGptSettings();
    if (s && !s.cancel && !s.error) {
      setLlmSource((s.llm_source || "pivony_local").trim());
      setActiveGptModelId(s.active_gpt_model_id || null);
    }
  }, []);

  const loadCxGptModels = useCallback(async () => {
    setModelsLoading(true);
    const m = await fetchCxGptModels();
    if (m && !m.cancel && !m.error && Array.isArray(m.models)) {
      setModelsPayload(m);
    } else {
      setModelsPayload({ models: [], llm_source: llmSource });
      setSelectedChatModel(null);
    }
    setModelsLoading(false);
  }, [llmSource]);

  const loadCxGptCatalog = useCallback(async () => {
    const c = await fetchCxGptCatalog();
    if (
      c &&
      !c.cancel &&
      !c.error &&
      c.catalog_available &&
      Array.isArray(c.models)
    ) {
      setCatalogPayload(c.models);
    } else {
      setCatalogPayload([]);
    }
  }, []);

  useEffect(() => {
    if (!hasCxGptAccess) return;
    loadCxGptSettings();
    loadCxGptCatalog();
  }, [hasCxGptAccess, loadCxGptSettings, loadCxGptCatalog]);

  useEffect(() => {
    let cancelled = false;
    fetchOrgIndustry().then((result) => {
      if (cancelled) return;
      setIndustryName(result?.name || null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!hasCxGptAccess) return;
    loadCxGptModels();
  }, [hasCxGptAccess, loadCxGptModels, llmSource]);

  useEffect(() => {
    if (!modelsPayload?.models?.length) return;
    const ids = modelsPayload.models.map((x) => x.id);
    setSelectedChatModel((prev) => {
      if (prev && ids.includes(prev)) return prev;
      if (activeGptModelId != null && catalogPayload.length > 0) {
        const activeRow = catalogPayload.find(
          (m) => Number(m.gpt_model_id) === Number(activeGptModelId)
        );
        const activeModelName = activeRow?.model_name;
        if (activeModelName && ids.includes(activeModelName)) {
          return activeModelName;
        }
      }
      const dm = modelsPayload.default_model;
      if (dm && ids.includes(dm)) return dm;
      return ids[0] || null;
    });
  }, [modelsPayload, activeGptModelId, catalogPayload]);

  const activeSession = useMemo(
    () =>
      copilotState.sessions.find((s) => s.id === copilotState.activeSessionId),
    [copilotState.sessions, copilotState.activeSessionId]
  );

  const messages = activeSession ? activeSession.messages : [];

  const markSessionRated = useCallback((sessionId, rating) => {
    setCopilotState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) => (s.id === sessionId ? { ...s, rating } : s)),
    }));
  }, []);
  const {
    card: ratingCard,
    setStars: setRatingStars,
    setComment: setRatingComment,
    submit: submitRating,
    skip: skipRating,
    noteQuestion: noteRatingQuestion,
    clear: clearRatingCard,
  } = useAdvisorRatingCard({
    enabled: sessionsSource === "db" && hasCxGptAccess,
    session: activeSession,
    loading,
    draft: inputValue,
    listRef: messageListRef,
    onRated: markSessionRated,
  });
  const groupedMessages = useMemo(() => withMessageGroups(messages), [messages]);
  const isPage = variant === "page";
  const historyDrawer = Boolean(historyAsDrawer);
  const location = useLocation();
  const advisorVoiceLanguage =
    normalizeAppLanguage(i18n.language) === "tr" ? "tr-TR" : "en-US";
  const hasAdvisorVoiceAccess = hasCxGptAccess;

  const appendVoiceTranscript = useCallback(({ role, text, meta }) => {
    const line = String(text || "").trim();
    if (!line) return;
    const msgRole = role === "user" ? "user" : "assistant";
    const isFinal = meta?.final !== false;
    if (msgRole === "user" && !isFinal) return;
    if (msgRole === "user") noteRatingQuestion();
    const content = meta && typeof meta === "string" ? `${line}\n\n${meta}` : line;

    const mergeVoiceText = (prev, next) => {
      const p = String(prev || "").trim();
      const n = String(next || "").trim();
      if (!p) return n;
      if (!n) return p;
      if (n.startsWith(p) || p.startsWith(n)) return n.length >= p.length ? n : p;
      if (p.includes(n)) return p;
      if (n.includes(p)) return n;
      return `${p} ${n}`.trim();
    };

    setCopilotState((prev) =>
      patchActiveSession(prev, (s) => {
        const msgs = [...s.messages];
        const last = msgs[msgs.length - 1];
        const lastPlain =
          last && typeof last.content === "string"
            ? last.content.split("\n\n")[0].trim()
            : "";

        if (
          last &&
          last.role === msgRole &&
          last.modality === "voice" &&
          msgRole === "assistant"
        ) {
          const mergedLine = mergeVoiceText(lastPlain, line);
          const mergedContent = meta ? `${mergedLine}\n\n${meta}` : mergedLine;
          msgs[msgs.length - 1] = {
            ...last,
            content: mergedContent,
            ts: Date.now(),
          };
        } else if (
          last &&
          last.role === "user" &&
          msgRole === "user" &&
          last.modality === "voice" &&
          Date.now() - (last.ts || 0) < 12000
        ) {
          const mergedLine = mergeVoiceText(lastPlain, line);
          const mergedContent = meta ? `${mergedLine}\n\n${meta}` : mergedLine;
          msgs[msgs.length - 1] = {
            ...last,
            content: mergedContent,
            ts: Date.now(),
          };
        } else {
          msgs.push({
            role: msgRole,
            content,
            ts: Date.now(),
            modality: "voice",
            isPlainText: true,
          });
        }

        return {
          ...s,
          messages: msgs,
          updatedAt: Date.now(),
        };
      })
    );
  }, [noteRatingQuestion]);

  const advisorVoice = useAdvisorVoice({
    languageCode: advisorVoiceLanguage,
    enabled: hasAdvisorVoiceAccess && isPage,
    onTranscript: appendVoiceTranscript,
  });

  const isVoiceSessionUi =
    isPage && advisorVoice.isVoiceActive;

  const isPageHero =
    isPage &&
    !loading &&
    !sessionsLoading &&
    (messages.length === 0 || isVoiceSessionUi);

  const showVoiceCanvas = isPageHero && isVoiceSessionUi;

  const isPageActive = isPage && messages.length > 0 && !isVoiceSessionUi;

  const voiceSessionMessages = useMemo(
    () => messages.filter((m) => m.modality === "voice").slice(-8),
    [messages]
  );

  const voiceAutoStartedRef = useRef(false);
  const voiceMicAutoStartedRef = useRef(false);
  useEffect(() => {
    if (!isPage || !hasAdvisorVoiceAccess) return undefined;
    const params = new URLSearchParams(location.search);
    if (params.get("voice") !== "1") return undefined;
    if (voiceAutoStartedRef.current) return undefined;
    if (advisorVoice.connection !== ADVISOR_VOICE_CONNECTION.IDLE) return undefined;
    voiceAutoStartedRef.current = true;
    advisorVoice.connectAndListen();
    return undefined;
  }, [
    isPage,
    hasAdvisorVoiceAccess,
    location.search,
    advisorVoice.connection,
    advisorVoice.connectAndListen,
  ]);

  useEffect(() => {
    if (!isPage) return undefined;
    const params = new URLSearchParams(location.search);
    if (params.get("voice") !== "1") return undefined;
    if (!advisorVoice.isConnected || advisorVoice.micActive) return undefined;
    if (voiceMicAutoStartedRef.current) return undefined;
    voiceMicAutoStartedRef.current = true;
    advisorVoice.startMic();
    return undefined;
  }, [
    isPage,
    location.search,
    advisorVoice.isConnected,
    advisorVoice.micActive,
    advisorVoice.startMic,
  ]);

  const handleAdvisorVoiceMicClick = useCallback(async () => {
    if (!hasAdvisorVoiceAccess) {
      message.info(t("cx_gpt.voice_no_access"));
      return;
    }
    if (advisorVoice.isConnecting) return;
    if (advisorVoice.micActive) {
      advisorVoice.stopMic();
      return;
    }
    if (advisorVoice.connection === ADVISOR_VOICE_CONNECTION.IDLE) {
      advisorVoice.connectAndListen();
      return;
    }
    await advisorVoice.startMic();
  }, [advisorVoice, hasAdvisorVoiceAccess, t]);
  const isFreshSession =
    messages.length === 0 && activeSession?.analyticsScope?.dashboardId == null;

  const lastDashboardSelection = useMemo(
    () => lastDashboardSelectionFromMessages(messages),
    [messages]
  );

  const contextPayload = useMemo(() => {
    if (isFreshSession) {
      return {
        page: context.page,
        subPage: context.subPage,
        pageType: context.pageType,
        dashboardId: null,
        dashboardName: null,
        selectedDaysRange: context.selectedDaysRange,
        topTopics: [],
        since: null,
        until: null,
        pivotFilter: {},
      filterLabels: [],
      hasDashboard: false,
      analyticsScope: null,
      lastDashboardSelection: null,
      freshSession: true,
      kpiTeamId: context.isKpiPage ? context.kpiTeamId : null,
      isKpiPage: context.isKpiPage || false,
    };
  }
    const sessionScope =
      activeSession?.analyticsScope || deriveAnalyticsScopeFromMessages(messages);
    const chip = chipPeriodWindow(sessionScope);
    const resolvedDashboardId = resolveContextDashboardId({
      context,
      sessionAnalyticsScope: sessionScope,
      isAdvisorPage: isPage,
    });
    return {
      page: context.page,
      subPage: context.subPage,
      pageType: context.pageType,
      dashboardId: resolvedDashboardId,
      dashboardName: resolvedDashboardId != null ? context.dashboardName : null,
      selectedDaysRange:
        chip?.days != null ? `last${chip.days}d` : context.selectedDaysRange,
      topTopics: context.topTopics,
      since: chip?.since || context.since,
      until: chip?.until || context.until,
      pivotFilter: context.pivotFilter || {},
      filterLabels: context.filterLabels,
      hasDashboard: resolvedDashboardId != null,
      analyticsScope: analyticsScopeForApi(
        chip ? { ...sessionScope, ...chip } : sessionScope
      ),
      lastDashboardSelection,
      freshSession: false,
      kpiTeamId: context.isKpiPage ? context.kpiTeamId : null,
      isKpiPage: context.isKpiPage || false,
    };
  }, [
    context.page,
    context.subPage,
    context.pageType,
    context.dashboardId,
    context.dashboardName,
    context.selectedDaysRange,
    context.topTopics,
    context.since,
    context.until,
    context.pivotFilter,
    context.filterLabels,
    context.isKpiPage,
    context.kpiTeamId,
    activeSession?.analyticsScope,
    lastDashboardSelection,
    messages,
    isPage,
    isFreshSession,
  ]);

  const contextUsage = useMemo(
    () =>
      estimateAdvisorContextUsage({
        messages,
        draftText: inputValue,
        contextPayload,
        maxTokens: activeSession?.contextMaxTokens ?? ADVISOR_MAX_CONTEXT_TOKENS,
      }),
    [messages, inputValue, contextPayload, activeSession?.contextMaxTokens]
  );
  const thinkingToolLabel = useMemo(() => {
    if (!streamingToolStatus) return "";
    if (streamingToolStatus === "list_dashboards") {
      return t(
        "cx_gpt.thinking_tool_list_dashboards",
        "Listing dashboards…",
      );
    }
    return t("cx_gpt.thinking_tool", { tool: streamingToolStatus });
  }, [streamingToolStatus, t]);

  const contextualSuggestions = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (
        msg.role === "assistant" &&
        Array.isArray(msg.suggestedFollowups) &&
        msg.suggestedFollowups.length > 0
      ) {
        return msg.suggestedFollowups.filter(
          (item) => typeof item === "string" && item.trim()
        );
      }
    }
    return [];
  }, [messages]);

  const suggestedQuestions =
    contextualSuggestions.length > 0
      ? contextualSuggestions
      : context.suggestedQuestions || [];
  const hasSuggestedQuestions = suggestedQuestions.length > 0;
  const showEmptySuggestions =
    !isPage &&
    hasSuggestedQuestions &&
    !suggestionsDismissed &&
    messages.length === 0;

  const capabilityCards = useMemo(
    () => [
      {
        key: "insights",
        icon: BulbOutlined,
        title: t("cx_gpt.capability_insights_title"),
        desc: t("cx_gpt.capability_insights_desc"),
      },
      {
        key: "reports",
        icon: FileTextOutlined,
        title: t("cx_gpt.capability_reports_title"),
        desc: t("cx_gpt.capability_reports_desc"),
      },
      {
        key: "dashboards",
        icon: BarChartOutlined,
        title: t("cx_gpt.capability_dashboards_title"),
        desc: t("cx_gpt.capability_dashboards_desc"),
      },
    ],
    [t]
  );

  const disclaimerFooter = useMemo(() => {
    if (industryName) {
      return t("cx_gpt.disclaimer_footer", { industry_name: industryName });
    }
    return t("cx_gpt.disclaimer_footer_generic");
  }, [industryName, t]);

  const heroGreeting = useMemo(() => {
    const name = resolveAdvisorFirstName(user, accountUser);
    if (name) {
      return t("cx_gpt.page_greeting", { name });
    }
    return t("cx_gpt.page_greeting_generic");
  }, [
    user?.firstName,
    user?.fullName,
    user?.displayName,
    user?.name,
    user?.email,
    accountUser?.firstName,
    accountUser?.fullName,
    accountUser?.displayName,
    accountUser?.name,
    accountUser?.email,
    t,
  ]);

  const chatUserFirstName = useMemo(
    () => resolveAdvisorFirstName(user, accountUser),
    [
      user?.firstName,
      user?.fullName,
      user?.displayName,
      user?.name,
      user?.email,
      accountUser?.firstName,
      accountUser?.fullName,
      accountUser?.displayName,
      accountUser?.name,
      accountUser?.email,
    ]
  );

  const chatUserInitial = useMemo(
    () => resolveUserInitial(chatUserFirstName),
    [chatUserFirstName]
  );

  const chatUserDisplayName = chatUserFirstName || t("cx_gpt.you");

  const advisorPanelTitle = useMemo(
    () => resolveSectorExperienceLabel(industryName, t, i18n.language),
    [industryName, i18n.language, t]
  );

  const dismissSuggestions = useCallback(() => {
    setSuggestionsDismissed(true);
    saveSuggestionsDismissed(true);
  }, []);

  useEffect(() => {
    try {
      const pending = sessionStorage.getItem("copilotPendingPrompt");
      if (pending) {
        setInputValue(pending);
        sessionStorage.removeItem("copilotPendingPrompt");
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!hasCxGptAccess) {
      saveStateToStorage(copilotState);
    }
  }, [copilotState, hasCxGptAccess]);

  useEffect(() => {
    if (!hasCxGptAccess) {
      setCopilotState(loadState());
      setSessionsSource("local");
      setSessionsLoading(false);
      setSessionsReady(true);
      return undefined;
    }

    let cancelled = false;

    const bootstrapDbSessions = async () => {
      setSessionsLoading(true);
      setSessionsReady(false);
      try {
        if (localStorage.getItem(COPILOT_DB_MIGRATED_KEY) !== "1") {
          const local = loadState();
          if (local.sessions?.length) {
            await importCxGptSessions({
              sessions: local.sessions.map(mapClientSessionForImport),
              active_session_id: local.activeSessionId,
            });
          }
          localStorage.setItem(COPILOT_DB_MIGRATED_KEY, "1");
        }

        const listResult = await fetchCxGptSessions();
        if (cancelled) return;

        if (
          !listResult ||
          listResult.cancel ||
          listResult.error ||
          listResult.database_ready === false
        ) {
          setSessionsSource("local");
          setCopilotState(loadState());
          return;
        }

        let sessions = (listResult.sessions || []).map(mapApiSessionSummary);
        let activeSessionId = sessions[0]?.id;

        if (!sessions.length) {
          const created = await createCxGptSession({ title: t("cx_gpt.new_chat") });
          if (created?.session) {
            const mapped = mapApiSessionDetail({ ...created.session, messages: [] });
            mapped.messagesLoaded = true;
            sessions = [mapped];
            activeSessionId = mapped.id;
          } else {
            setSessionsSource("local");
            setCopilotState(loadState());
            return;
          }
        }

        if (activeSessionId) {
          const detailResult = await fetchCxGptSession(activeSessionId);
          if (!cancelled && detailResult?.session) {
            sessions = sessions.map((s) =>
              s.id === activeSessionId
                ? mapApiSessionDetail(detailResult.session)
                : s
            );
          }
        }

        setCopilotState((prev) => ({
          sessions,
          activeSessionId: activeSessionId || sessions[0]?.id,
          lastRoute: prev.lastRoute,
        }));
        setSessionsSource("db");
      } catch (_) {
        if (!cancelled) {
          setSessionsSource("local");
          setCopilotState(loadState());
        }
      } finally {
        if (!cancelled) {
          setSessionsLoading(false);
          setSessionsReady(true);
        }
      }
    };

    bootstrapDbSessions();
    return () => {
      cancelled = true;
    };
  }, [hasCxGptAccess, t]);

  useEffect(() => {
    if (sessionsSource !== "db" || sessionsLoading || !hasCxGptAccess) {
      return undefined;
    }

    clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      const state = copilotStateRef.current;
      const session = state.sessions.find((s) => s.id === state.activeSessionId);
      if (!session || session.messagesLoaded === false) return;

      const usage = estimateAdvisorContextUsage({
        messages: session.messages || [],
        draftText: "",
        contextPayload,
        maxTokens: session.contextMaxTokens ?? ADVISOR_MAX_CONTEXT_TOKENS,
      });

      updateCxGptSession(
        session.id,
        mapClientSessionForPut(session, {
          lastRoute: state.lastRoute,
          contextUsage: usage,
        })
      ).then((result) => {
        if (!result || result.error) return;
        // Only patch token fields when they actually change. Updating
        // updatedAt here re-triggers this effect and floods PUT /sessions.
        setCopilotState((prev) => {
          const existing = prev.sessions.find((s) => s.id === session.id);
          if (
            existing &&
            existing.contextUsedTokens === usage.usedTokens &&
            existing.contextMaxTokens === usage.maxTokens
          ) {
            return prev;
          }
          return {
            ...prev,
            sessions: prev.sessions.map((s) =>
              s.id === session.id
                ? {
                    ...s,
                    contextUsedTokens: usage.usedTokens,
                    contextMaxTokens: usage.maxTokens,
                  }
                : s
            ),
          };
        });
      });
    }, 600);

    return () => clearTimeout(persistTimerRef.current);
  }, [copilotState, sessionsSource, sessionsLoading, hasCxGptAccess, contextPayload]);

  useEffect(() => {
    const route =
      typeof window !== "undefined" ? window.location.pathname : "";
    setCopilotState((p) =>
      p.lastRoute === route ? p : { ...p, lastRoute: route }
    );
  }, [context.page, context.subPage]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setCopilotOpen(false);
        return;
      }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.shiftKey && (e.key === "g" || e.key === "G")) {
        e.preventDefault();
        setCopilotOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setCopilotOpen]);

  const scrollToBottom = useCallback(() => {
    const list = messageListRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
    pinNavbarIntoView();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, streamingThought, streamingToolStatus, scrollToBottom]);

  const resetAdvisorStreamState = useCallback(() => {
    activeStreamGenRef.current += 1;
    sendInFlightRef.current = false;
    setLoading(false);
    setStreamingThought("");
    setStreamingToolStatus("");
    setStreamContentStarted(false);
    setInputValue("");
  }, []);

  const appendPlanUpsellReply = useCallback(
    (text) => {
      const userMessage = { role: "user", content: text, ts: Date.now() };
      const upsellMessage = {
        role: "assistant",
        content: t("cx_gpt.plan_required_body"),
        isPlainText: true,
        ts: Date.now(),
      };
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => ({
          ...s,
          messages: [...s.messages, userMessage, upsellMessage],
          title: s.titleIsCustom
            ? s.title
            : String(text).slice(0, 56).trim() || s.title || t("cx_gpt.new_chat"),
          updatedAt: Date.now(),
        }))
      );
    },
    [t]
  );

  const sendMessage = async (rawText, sendOptions = {}) => {
    const text = typeof rawText === "string" ? rawText.trim() : "";
    if (!text) return;
    if (sendInFlightRef.current && !sendOptions.bypassInFlight) return;
    const effectiveContext =
      sendOptions && sendOptions.contextOverride
        ? {
            ...contextPayload,
            ...sendOptions.contextOverride,
            freshSession: false,
            hasDashboard: sendOptions.contextOverride.dashboardId != null,
          }
        : contextPayload;

    if (contextUsage.isFull) {
      message.warning(t("cx_gpt.context_usage_full"));
      return;
    }

    if (!hasCxGptAccess) {
      appendPlanUpsellReply(text);
      setInputValue("");
      return;
    }

    const selection =
      sendOptions.dashboardSelection &&
      typeof sendOptions.dashboardSelection === "object" &&
      sendOptions.dashboardSelection.id != null
        ? {
            id: sendOptions.dashboardSelection.id,
            name:
              typeof sendOptions.dashboardSelection.name === "string"
                ? sendOptions.dashboardSelection.name.trim() || null
                : null,
          }
        : null;
    const userMessage = {
      role: "user",
      content: text,
      ts: Date.now(),
      ...(selection ? { dashboardSelection: selection } : {}),
    };

    trackAdvisorMessageSent(copilotState.activeSessionId, {
      onAdvisorPage: isPage,
      messageLength: text.length,
    });
    noteRatingQuestion();

    setCopilotState((prev) =>
      patchActiveSession(prev, (s) => {
        const nextMsgs = [...s.messages, userMessage];
        const title = s.titleIsCustom
          ? s.title
          : String(text).slice(0, 56).trim() || s.title || t("cx_gpt.new_chat");
        return {
          ...s,
          messages: nextMsgs,
          title,
          updatedAt: Date.now(),
        };
      })
    );
    setInputValue("");
    setLoading(true);
    setStreamingThought("");
    setStreamingToolStatus("");
    setStreamContentStarted(false);
    sendInFlightRef.current = true;
    activeStreamGenRef.current += 1;
    const streamGen = activeStreamGenRef.current;
    let releaseInteractiveTimer = null;

    const releaseInteractiveUi = () => {
      if (streamGen !== activeStreamGenRef.current) return;
      sendInFlightRef.current = false;
      setLoading(false);
    };

    const scheduleReleaseInteractiveUi = () => {
      clearTimeout(releaseInteractiveTimer);
      releaseInteractiveTimer = setTimeout(releaseInteractiveUi, 450);
    };

    const historyWithUser = [...messages, userMessage];
    let streamReasoningAcc = "";
    const streamToolActions = [];
    let streamChartsAcc = [];

    const upsertStreamingReasoning = (reasoningText, toolActions) => {
      const reasoning = normalizeThoughtPreview(reasoningText);
      const actions = Array.isArray(toolActions)
        ? toolActions.filter((item) => typeof item === "string" && item.trim())
        : [];
      if (!reasoning && !actions.length) return;

      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          // Thought/tool status must not create an empty assistant bubble.
          if (!(last && last.role === "assistant" && last.streaming)) {
            return s;
          }
          msgs[msgs.length - 1] = {
            ...last,
            ...(reasoning ? { reasoning } : {}),
            ...(actions.length ? { toolActions: actions } : {}),
          };
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const upsertStreamingAssistant = (content) => {
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            msgs[msgs.length - 1] = { ...last, content };
          } else {
            msgs.push({
              role: "assistant",
              content,
              streaming: true,
              ts: Date.now(),
            });
          }
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const upsertStreamingDashboardPicker = (picker) => {
      if (
        !picker ||
        typeof picker !== "object" ||
        !Array.isArray(picker.dashboards) ||
        !picker.dashboards.length
      ) {
        return;
      }
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            msgs[msgs.length - 1] = {
              ...last,
              dashboardPicker: picker,
              suggestedFollowups: [],
              guidance: "",
            };
          } else {
            msgs.push({
              role: "assistant",
              content: "",
              dashboardPicker: picker,
              suggestedFollowups: [],
              guidance: "",
              streaming: true,
              ts: Date.now(),
            });
          }
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const upsertStreamingPeriodPicker = (picker) => {
      if (
        !picker ||
        typeof picker !== "object" ||
        !Array.isArray(picker.periods) ||
        !picker.periods.length
      ) {
        return;
      }
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            msgs[msgs.length - 1] = {
              ...last,
              periodPicker: picker,
              suggestedFollowups: [],
              guidance: "",
            };
          } else {
            msgs.push({
              role: "assistant",
              content: "",
              periodPicker: picker,
              suggestedFollowups: [],
              guidance: "",
              streaming: true,
              ts: Date.now(),
            });
          }
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const upsertStreamingKpiTeamPicker = (picker) => {
      if (
        !picker ||
        typeof picker !== "object" ||
        !Array.isArray(picker.teams) ||
        !picker.teams.length
      ) {
        return;
      }
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            msgs[msgs.length - 1] = {
              ...last,
              kpiTeamPicker: picker,
              suggestedFollowups: [],
              guidance: "",
            };
          } else {
            msgs.push({
              role: "assistant",
              content: "",
              kpiTeamPicker: picker,
              suggestedFollowups: [],
              guidance: "",
              streaming: true,
              ts: Date.now(),
            });
          }
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const upsertStreamingKpiMetricPicker = (picker) => {
      if (
        !picker ||
        typeof picker !== "object" ||
        !Array.isArray(picker.metrics) ||
        !picker.metrics.length
      ) {
        return;
      }
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            msgs[msgs.length - 1] = {
              ...last,
              kpiMetricPicker: picker,
              suggestedFollowups: [],
              guidance: "",
            };
          } else {
            msgs.push({
              role: "assistant",
              content: "",
              kpiMetricPicker: picker,
              suggestedFollowups: [],
              guidance: "",
              streaming: true,
              ts: Date.now(),
            });
          }
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const upsertStreamingCharts = (charts) => {
      const nextCharts = Array.isArray(charts)
        ? charts.filter((item) => item && typeof item === "object")
        : [];
      if (!nextCharts.length) return;
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            msgs[msgs.length - 1] = { ...last, charts: nextCharts };
          } else {
            msgs.push({
              role: "assistant",
              content: "",
              charts: nextCharts,
              streaming: true,
              ts: Date.now(),
            });
          }
          return { ...s, messages: msgs, updatedAt: Date.now() };
        })
      );
    };

    const finalizeStreamingAssistant = (payload) => {
      const text =
        typeof payload.content === "string"
          ? payload.content
          : payload.content != null
            ? String(payload.content)
            : "";
      const suggestedFollowups = Array.isArray(payload.suggested_followups)
        ? payload.suggested_followups
            .filter((item) => typeof item === "string" && item.trim())
            .map((item) => item.trim())
        : [];
      const guidance =
        typeof payload.guidance === "string" ? payload.guidance.trim() : "";
      const dashboardPicker =
        payload.dashboard_picker &&
        typeof payload.dashboard_picker === "object" &&
        Array.isArray(payload.dashboard_picker.dashboards) &&
        payload.dashboard_picker.dashboards.length
          ? payload.dashboard_picker
          : null;
      const periodPicker =
        payload.period_picker &&
        typeof payload.period_picker === "object" &&
        Array.isArray(payload.period_picker.periods) &&
        payload.period_picker.periods.length
          ? payload.period_picker
          : null;
      const kpiMetricPicker =
        payload.kpi_metric_picker &&
        typeof payload.kpi_metric_picker === "object" &&
        Array.isArray(payload.kpi_metric_picker.metrics) &&
        payload.kpi_metric_picker.metrics.length
          ? payload.kpi_metric_picker
          : null;
      const kpiTeamPicker =
        payload.kpi_team_picker &&
        typeof payload.kpi_team_picker === "object" &&
        Array.isArray(payload.kpi_team_picker.teams) &&
        payload.kpi_team_picker.teams.length
          ? payload.kpi_team_picker
          : null;
      const skipFollowups = Boolean(
        dashboardPicker || periodPicker || kpiMetricPicker || kpiTeamPicker
      );
      const charts = Array.isArray(payload.charts)
        ? payload.charts.filter((item) => item && typeof item === "object")
        : [];
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => {
          const msgs = [...s.messages];
          const priorScope =
            s.analyticsScope || deriveAnalyticsScopeFromMessages(msgs);
          const nextScope = mergeSessionAnalyticsScope(
            priorScope,
            deriveSessionAnalyticsScope(effectiveContext, text, s.analyticsScope),
            effectiveContext
          );
          const last = msgs[msgs.length - 1];
          const reasoning = normalizeThoughtPreview(
            (last && last.reasoning) || streamReasoningAcc || ""
          );
          const toolActions = Array.isArray(last?.toolActions) && last.toolActions.length
            ? last.toolActions
            : streamToolActions.filter((item) => typeof item === "string" && item.trim());
          const assistantMessage = {
            role: "assistant",
            content: text.length
              ? text
              : skipFollowups
                ? ""
                : t("cx_gpt.empty_reply"),
            suggestedFollowups: skipFollowups ? [] : suggestedFollowups,
            guidance: skipFollowups ? "" : guidance,
            dashboardPicker,
            periodPicker,
            kpiMetricPicker,
            kpiTeamPicker,
            ...(charts.length ? { charts } : {}),
            ...(reasoning ? { reasoning } : {}),
            ...(toolActions.length ? { toolActions } : {}),
          };
          if (last && last.role === "assistant") {
            const { streaming: _streaming, ...restLast } = last;
            const resolvedPicker = dashboardPicker || last.dashboardPicker;
            const resolvedPeriodPicker = periodPicker || last.periodPicker;
            const resolvedKpiMetricPicker = kpiMetricPicker || last.kpiMetricPicker;
            const resolvedKpiTeamPicker = kpiTeamPicker || last.kpiTeamPicker;
            const hasPicker =
              resolvedPicker ||
              resolvedPeriodPicker ||
              resolvedKpiMetricPicker ||
              resolvedKpiTeamPicker;
            msgs[msgs.length - 1] = {
              ...restLast,
              ...assistantMessage,
              content: text.length
                ? text
                : last.content || (hasPicker ? "" : t("cx_gpt.empty_reply")),
              dashboardPicker: resolvedPicker,
              periodPicker: resolvedPeriodPicker,
              kpiMetricPicker: resolvedKpiMetricPicker,
              kpiTeamPicker: resolvedKpiTeamPicker,
              suggestedFollowups: hasPicker ? [] : assistantMessage.suggestedFollowups,
              guidance: hasPicker ? "" : assistantMessage.guidance,
              ts: last.ts || Date.now(),
            };
          } else {
            msgs.push({
              ...assistantMessage,
              ts: Date.now(),
            });
          }
          return {
            ...s,
            messages: msgs,
            updatedAt: Date.now(),
            analyticsScope: nextScope || s.analyticsScope,
          };
        })
      );
    };

    let streamFinalized = false;

    try {
      const result = await streamCxGptChat(
        buildApiMessages(historyWithUser),
        {
          context: effectiveContext,
          model: resolveChatModelForApi(),
          sessionId: copilotState.activeSessionId,
          contextUsedTokens: contextUsage.usedTokens,
          contextMaxTokens: contextUsage.maxTokens,
          lastRoute: copilotState.lastRoute,
        },
        {
          onThought: (_delta, accumulated) => {
            if (streamGen !== activeStreamGenRef.current) return;
            streamReasoningAcc = accumulated;
            flushSync(() => {
              setStreamingThought(accumulated);
              upsertStreamingReasoning(accumulated, streamToolActions);
            });
          },
          onStatus: (toolName) => {
            if (streamGen !== activeStreamGenRef.current) return;
            if (
              typeof toolName === "string" &&
              toolName.trim() &&
              streamToolActions[streamToolActions.length - 1] !== toolName.trim()
            ) {
              streamToolActions.push(toolName.trim());
            }
            flushSync(() => {
              setStreamingToolStatus(toolName);
              upsertStreamingReasoning(streamReasoningAcc, streamToolActions);
            });
          },
          onRetry: (event) => {
            if (streamGen !== activeStreamGenRef.current) return;
            const retryMessage =
              (event && typeof event.message === "string" && event.message.trim()) ||
              t("cx_gpt.rate_limit_retry");
            flushSync(() => {
              setStreamingToolStatus("");
              upsertStreamingAssistant(retryMessage);
            });
          },
          onContent: (_delta, accumulated) => {
            if (streamGen !== activeStreamGenRef.current) return;
            const nextContent = typeof accumulated === "string" ? accumulated : "";
            if (!nextContent.trim()) return;
            setStreamContentStarted(true);
            upsertStreamingAssistant(nextContent);
            upsertStreamingReasoning(streamReasoningAcc, streamToolActions);
            scheduleReleaseInteractiveUi();
          },
          onDashboardPicker: (picker) => {
            if (streamGen !== activeStreamGenRef.current) return;
            flushSync(() => {
              upsertStreamingDashboardPicker(picker);
            });
            clearTimeout(releaseInteractiveTimer);
            releaseInteractiveUi();
          },
          onPeriodPicker: (picker) => {
            if (streamGen !== activeStreamGenRef.current) return;
            flushSync(() => {
              upsertStreamingPeriodPicker(picker);
            });
            clearTimeout(releaseInteractiveTimer);
            releaseInteractiveUi();
          },
          onKpiTeamPicker: (picker) => {
            if (streamGen !== activeStreamGenRef.current) return;
            flushSync(() => {
              upsertStreamingKpiTeamPicker(picker);
            });
            clearTimeout(releaseInteractiveTimer);
            releaseInteractiveUi();
          },
          onKpiMetricPicker: (picker) => {
            if (streamGen !== activeStreamGenRef.current) return;
            flushSync(() => {
              upsertStreamingKpiMetricPicker(picker);
            });
            clearTimeout(releaseInteractiveTimer);
            releaseInteractiveUi();
          },
          onChart: (_chart, accumulated) => {
            if (streamGen !== activeStreamGenRef.current) return;
            streamChartsAcc = Array.isArray(accumulated) ? accumulated : streamChartsAcc;
            flushSync(() => {
              upsertStreamingCharts(streamChartsAcc);
            });
          },
          onDone: (payload) => {
            if (streamGen !== activeStreamGenRef.current) return;
            clearTimeout(releaseInteractiveTimer);
            streamFinalized = true;
            finalizeStreamingAssistant({
              content: payload.content,
              suggested_followups: payload.pivony_suggested_followups,
              guidance: payload.pivony_guidance,
              dashboard_picker: payload.pivony_dashboard_picker,
              period_picker: payload.pivony_period_picker,
              kpi_metric_picker: payload.pivony_kpi_metric_picker,
              kpi_team_picker: payload.pivony_kpi_team_picker,
              charts:
                Array.isArray(payload.pivony_charts) && payload.pivony_charts.length
                  ? payload.pivony_charts
                  : streamChartsAcc,
            });
          },
          onError: (errMessage) => {
            if (streamGen !== activeStreamGenRef.current) return;
            message.warning(errMessage);
          },
        }
      );

      if (!result || result.cancel) {
        message.error(t("cx_gpt.network_error"));
        setCopilotState((prev) =>
          patchActiveSession(prev, (s) => ({
            ...s,
            messages: [
              ...s.messages,
              {
                role: "assistant",
                content: t("cx_gpt.network_error"),
                isPlainText: true,
                ts: Date.now(),
              },
            ],
            updatedAt: Date.now(),
          }))
        );
      } else if (result.validation_error != null) {
        message.warning(t("cx_gpt.validation_error"));
        setCopilotState((prev) =>
          patchActiveSession(prev, (s) => ({
            ...s,
            messages: [
              ...s.messages,
              {
                role: "assistant",
                content: t("cx_gpt.validation_error"),
                isPlainText: true,
                ts: Date.now(),
              },
            ],
            updatedAt: Date.now(),
          }))
        );
      } else {
        const errText = cxGptApiErrorText(result);
        if (errText) {
          message.warning(errText);
          setCopilotState((prev) =>
            patchActiveSession(prev, (s) => ({
              ...s,
              messages: [
                ...s.messages,
                {
                  role: "assistant",
                  content: `${t("cx_gpt.error_prefix")} ${errText}`,
                  isPlainText: true,
                  ts: Date.now(),
                },
              ],
              updatedAt: Date.now(),
            }))
          );
        } else if (result.content !== undefined && result.content !== null) {
          if (!streamFinalized) {
            finalizeStreamingAssistant(result);
          }
        } else {
          message.error(t("cx_gpt.unknown_response"));
          setCopilotState((prev) =>
            patchActiveSession(prev, (s) => ({
              ...s,
              messages: [
                ...s.messages,
                {
                  role: "assistant",
                  content: t("chat.2"),
                  isPlainText: true,
                  ts: Date.now(),
                },
              ],
              updatedAt: Date.now(),
            }))
          );
        }
      }
    } catch (_) {
      message.error(t("cx_gpt.network_error"));
      setCopilotState((prev) =>
        patchActiveSession(prev, (s) => ({
          ...s,
          messages: [
            ...s.messages,
            {
              role: "assistant",
              content: t("chat.3"),
              isPlainText: true,
              ts: Date.now(),
            },
          ],
          updatedAt: Date.now(),
        }))
      );
    } finally {
      clearTimeout(releaseInteractiveTimer);
      if (streamGen === activeStreamGenRef.current) {
        sendInFlightRef.current = false;
      }
      setLoading(false);
      setStreamingThought("");
      setStreamingToolStatus("");
      setStreamContentStarted(false);
    }
  };

  const handleNewChat = useCallback(async () => {
    resetAdvisorStreamState();

    if (sessionsSource === "db" && hasCxGptAccess) {
      const created = await createCxGptSession({ title: t("cx_gpt.new_chat") });
      if (created?.session) {
        const mapped = mapApiSessionDetail({ ...created.session, messages: [] });
        mapped.messagesLoaded = true;
        mapped.analyticsScope = null;
        trackAdvisorSessionStarted(mapped.id, { source: "new_chat" });
        setCopilotState((prev) => ({
          ...prev,
          sessions: [mapped, ...prev.sessions].slice(0, MAX_SESSIONS),
          activeSessionId: mapped.id,
        }));
        return;
      }
    }

    setCopilotState((prev) => {
      const id = newId();
      trackAdvisorSessionStarted(id, { source: "new_chat_local" });
      const trimmed = prev.sessions.slice(-MAX_SESSIONS + 1);
      return {
        ...prev,
        sessions: [
          ...trimmed,
          {
            id,
            title: t("cx_gpt.new_chat"),
            titleIsCustom: false,
            messages: [],
            updatedAt: Date.now(),
            contextUsedTokens: 0,
            contextMaxTokens: ADVISOR_MAX_CONTEXT_TOKENS,
            messagesLoaded: true,
            analyticsScope: null,
          },
        ],
        activeSessionId: id,
      };
    });
  }, [hasCxGptAccess, resetAdvisorStreamState, sessionsSource, t]);

  useEffect(() => {
    const onRun = () => setPopupRunTick((n) => n + 1);
    window.addEventListener(ADVISOR_POPUP_RUN_EVENT, onRun);
    return () => window.removeEventListener(ADVISOR_POPUP_RUN_EVENT, onRun);
  }, []);

  // Popup prompts always start a fresh chat; an already-empty active chat counts as fresh.
  useEffect(() => {
    if (!sessionsReady) return;
    const queued = takeQueuedAdvisorPopupPrompt();
    if (!queued) return;
    const { sessions, activeSessionId } = copilotStateRef.current;
    const active = sessions.find((s) => s.id === activeSessionId);
    const activeIsEmpty =
      active && active.messagesLoaded !== false && active.messages.length === 0;
    (async () => {
      const dashboard = await findDashboardByName(queued.dashboardName).catch(() => null);
      if (!activeIsEmpty) await handleNewChat();
      setPopupPromptToSend({
        text: queued.prompt,
        dashboard,
        since: queued.since || null,
        until: queued.until || null,
        page: queued.page || null,
      });
    })();
  }, [popupRunTick, sessionsReady, handleNewChat]);

  // Sent on the render after the new chat is active so history excludes the previous chat.
  useEffect(() => {
    if (!popupPromptToSend || loading || sendInFlightRef.current) return;
    if (messages.length > 0) return;
    const { text, dashboard, since, until, page } = popupPromptToSend;
    setPopupPromptToSend(null);
    if (!dashboard) {
      sendMessage(text);
      return;
    }
    const pinned = { id: dashboard.id, name: dashboard.name };
    sendMessage(text, {
      dashboardSelection: pinned,
      contextOverride: {
        dashboardId: pinned.id,
        dashboardName: pinned.name,
        hasDashboard: true,
        lastDashboardSelection: pinned,
        analyticsScope: {
          dashboardId: pinned.id,
          orgWide: false,
          days: since && until ? null : 1,
          since,
          until,
        },
        ...(page ? { page } : {}),
      },
    });
  }, [popupPromptToSend, loading, messages]);

  const handleMessageFeedback = useCallback(
    async (messageIndex, sentiment, msg) => {
      const sessionId = copilotStateRef.current.activeSessionId;
      if (!sessionId || sessionsSource !== "db" || !hasCxGptAccess) return;

      setCopilotState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                messageFeedback: {
                  ...(s.messageFeedback || {}),
                  [String(messageIndex)]: sentiment,
                },
              }
            : s
        ),
      }));

      const snapshot = stripHtmlToText(
        msg.isPlainText ? msg.content : msg.content
      ).slice(0, 500);
      const sessionTitle =
        copilotStateRef.current.sessions.find((s) => s.id === sessionId)?.title ||
        "";

      try {
        await submitCxGptMessageFeedback({
          session_id: sessionId,
          message_sort_index: messageIndex,
          sentiment,
          message_ts: msg.ts ?? null,
          content_snapshot: snapshot || undefined,
        });
      } catch {
        message.error(t("cx_gpt.feedback_error"));
      }
    },
    [hasCxGptAccess, sessionsSource, t]
  );

  const handleCopyMessage = useCallback(
    async (msg) => {
      const text = msg.isPlainText
        ? String(msg.content || "")
        : stripHtmlToText(msg.content);
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        message.success(t("cx_gpt.copied"));
      } catch {
        message.error(t("cx_gpt.copy_error"));
      }
    },
    [t]
  );

  const handleClearCurrent = () => {
    resetAdvisorStreamState();
    clearRatingCard();
    setCopilotState((prev) =>
      patchActiveSession(prev, (s) => ({
        ...s,
        messages: [],
        title: t("cx_gpt.new_chat"),
        titleIsCustom: false,
        updatedAt: Date.now(),
        analyticsScope: null,
      }))
    );
  };

  const handleRenameSession = useCallback(
    async (sessionId, rawTitle) => {
      const nextTitle =
        typeof rawTitle === "string" ? rawTitle.trim().slice(0, 256) : "";
      if (!nextTitle) return;
      const target = copilotStateRef.current.sessions.find(
        (s) => s.id === sessionId
      );
      if (!target || target.title === nextTitle) return;

      setCopilotState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                title: nextTitle,
                titleIsCustom: true,
                updatedAt: Date.now(),
              }
            : s
        ),
      }));

      if (sessionsSource === "db" && hasCxGptAccess) {
        const result = await renameCxGptSession(sessionId, nextTitle);
        if (!result || result.error || result.cancel) {
          message.error(t("cx_gpt.rename_error"));
        }
      }
    },
    [hasCxGptAccess, resetAdvisorStreamState, sessionsSource, t]
  );

  const selectSession = useCallback(
    async (sessionId) => {
      if (sessionId !== copilotStateRef.current.activeSessionId) {
        resetAdvisorStreamState();
      }
      setCopilotState((prev) => ({ ...prev, activeSessionId: sessionId }));

      if (sessionsSource !== "db" || !hasCxGptAccess) return;

      const current = copilotStateRef.current.sessions.find((s) => s.id === sessionId);
      if (current?.messagesLoaded) return;

      const detailResult = await fetchCxGptSession(sessionId);
      if (!detailResult?.session) return;

      setCopilotState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.id === sessionId ? mapApiSessionDetail(detailResult.session) : s
        ),
      }));
    },
    [hasCxGptAccess, resetAdvisorStreamState, sessionsSource]
  );

  const togglePageRailCollapsed = useCallback(() => {
    setPageRailCollapsed((prev) => {
      const next = !prev;
      savePageRailCollapsed(next);
      return next;
    });
  }, []);

  const togglePanelHistory = useCallback(() => {
    setPanelHistoryOpen((prev) => !prev);
  }, []);

  const closePanelHistory = useCallback(() => {
    setPanelHistoryOpen(false);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.shiftKey && (e.key === "n" || e.key === "N")) {
        e.preventDefault();
        handleNewChat();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleNewChat]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && panelHistoryOpen && (!isPage || historyDrawer)) {
        e.stopPropagation();
        closePanelHistory();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [historyDrawer, isPage, panelHistoryOpen, closePanelHistory]);

  useEffect(() => {
    if (!isPage) return undefined;
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || !e.shiftKey) return;
      if (e.key !== "e" && e.key !== "E") return;
      e.preventDefault();
      setExportChatOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPage]);

  const exportShortcutHint = useMemo(() => {
    const isMac =
      typeof navigator !== "undefined" &&
      /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");
    return isMac ? "⌘⇧E" : "Ctrl⇧E";
  }, []);

  const renderChatSessionRail = ({
    className = "",
    collapsed = false,
    onToggleCollapse,
    showCollapseControl = true,
  } = {}) => (
    <ChatSessionRail
      className={className}
      sessions={copilotState.sessions}
      activeSessionId={copilotState.activeSessionId}
      onSelectSession={(sessionId) => {
        selectSession(sessionId);
        if (!isPage || historyDrawer) closePanelHistory();
      }}
      onNewChat={() => {
        handleNewChat();
        if (!isPage || historyDrawer) closePanelHistory();
      }}
      onRenameSession={handleRenameSession}
      searchQuery={historySearch}
      onSearchChange={setHistorySearch}
      displaySessionTitle={displaySessionTitle}
      formatSessionMeta={formatSessionMeta}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      showCollapseControl={showCollapseControl}
    />
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleComposerSend();
    }
  };

  const handleComposerSend = () => {
    const text = typeof inputValue === "string" ? inputValue.trim() : "";
    if (!text) return;
    if (advisorVoice.isConnected && advisorVoice.sendTextOverVoice(text)) {
      setInputValue("");
      return;
    }
    sendMessage(inputValue);
  };

  const onSuggestedClick = (q) => {
    if (advisorVoice.isConnected && advisorVoice.sendTextOverVoice(q)) {
      return;
    }
    sendMessage(q);
  };

  const findPendingQuestionBeforePicker = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if (msg.role !== "assistant" || (
        !msg.dashboardPicker &&
        !msg.periodPicker &&
        !msg.kpiMetricPicker &&
        !msg.kpiTeamPicker
      ))
        continue;
      for (let j = i - 1; j >= 0; j -= 1) {
        const prior = messages[j];
        if (prior.role === "user" && typeof prior.content === "string") {
          const text = prior.content.trim();
          if (text) return text;
        }
      }
      break;
    }
    return null;
  }, [messages]);

  const onDashboardPick = (d) => {
    if (!d || d.id == null) return;
    const displayName = d.name || `Dashboard ${d.id}`;
    const pendingQuestion = findPendingQuestionBeforePicker();
    // Re-ask the original data question with the chosen dashboard pinned in
    // context, instead of sending only the dashboard name as the user message.
    const textToSend = pendingQuestion || displayName;
    sendMessage(textToSend, {
      bypassInFlight: true,
      dashboardSelection: {
        id: d.id,
        name: d.name || null,
      },
      contextOverride: {
        dashboardId: d.id,
        dashboardName: d.name || null,
        hasDashboard: true,
        lastDashboardSelection: {
          id: d.id,
          name: d.name || null,
        },
        analyticsScope: {
          dashboardId: d.id,
          orgWide: false,
          days: null,
          since: contextPayload.since || null,
          until: contextPayload.until || null,
        },
      },
    });
    setCopilotState((prev) =>
      patchActiveSession(prev, (s) => ({
        ...s,
        analyticsScope: {
          dashboardId: d.id,
          orgWide: false,
          days: null,
          since: contextPayload.since || null,
          until: contextPayload.until || null,
        },
      }))
    );
  };

  const onPeriodPick = (days) => {
    const lookback = Number(days);
    if (!Number.isFinite(lookback) || lookback <= 0) return;
    const pendingQuestion = findPendingQuestionBeforePicker();
    const textToSend =
      pendingQuestion || t("cx_gpt.period_picker_n", { days: lookback });
    const dashId = contextPayload.dashboardId;
    const chip = lookbackDateWindow(lookback) || {
      days: lookback,
      since: null,
      until: null,
    };
    sendMessage(textToSend, {
      bypassInFlight: true,
      contextOverride: {
        dashboardId: dashId,
        dashboardName: contextPayload.dashboardName || null,
        hasDashboard: dashId != null,
        lastDashboardSelection: contextPayload.lastDashboardSelection,
        analyticsScope: {
          dashboardId: dashId,
          orgWide: false,
          ...chip,
        },
      },
    });
    setCopilotState((prev) =>
      patchActiveSession(prev, (s) => ({
        ...s,
        analyticsScope: {
          ...(s.analyticsScope || {}),
          dashboardId: dashId ?? s.analyticsScope?.dashboardId ?? null,
          orgWide: false,
          ...chip,
        },
      }))
    );
  };

  const onKpiTeamPick = (team) => {
    if (!team || team.team_id == null) return;
    const label = team.name || String(team.team_id);
    const pendingQuestion = findPendingQuestionBeforePicker();
    sendMessage(pendingQuestion || label, {
      bypassInFlight: true,
      contextOverride: {
        kpiTeamId: String(team.team_id),
        isKpiPage: true,
      },
    });
  };

  const onKpiMetricPick = (metricLabel) => {
    const label = typeof metricLabel === "string" ? metricLabel.trim() : "";
    if (!label) return;
    const pendingQuestion = findPendingQuestionBeforePicker();
    sendMessage(pendingQuestion || label, { bypassInFlight: true });
  };

  const renderHeroStarterChips = () => (
    <div className={styles.heroStarterRow}>
      {suggestedQuestions.slice(0, 4).map((q, i) => (
        <button
          key={i}
          type="button"
          className={styles.heroStarterChip}
          onClick={() => onSuggestedClick(q)}
          disabled={loading}
        >
          {q}
        </button>
      ))}
    </div>
  );

  const renderMessage = (msg, index) => {
    const isUser = msg.role === "user";
    const showHeader = msg.isFirstInGroup;
    const hasAssistantBody =
      Boolean(String(msg.content || "").trim()) ||
      Boolean(msg.dashboardPicker) ||
      Boolean(msg.periodPicker) ||
      Boolean(msg.kpiMetricPicker) ||
      Boolean(msg.kpiTeamPicker) ||
      (Array.isArray(msg.charts) && msg.charts.length > 0);
    if (!isUser && msg.streaming && !hasAssistantBody) {
      return null;
    }
    const followupsPending =
      !isUser &&
      !msg.dashboardPicker &&
      !msg.periodPicker &&
      !msg.kpiMetricPicker &&
      !msg.kpiTeamPicker &&
      msg.streaming &&
      Boolean(String(msg.content || "").trim()) &&
      !(Array.isArray(msg.suggestedFollowups) && msg.suggestedFollowups.length) &&
      !msg.guidance;

    return (
      <div
        key={`${msg.ts || index}-${index}`}
        className={`${isUser ? styles.messageUser : styles.messageAssistant} ${
          showHeader ? "" : styles.messageGrouped
        }`.trim()}
      >
        <div className={styles.messageRow}>
          {showHeader ? (
            <span
              className={`${styles.messageAvatar} ${
                isUser ? styles.messageAvatarUserInitial : ""
              }`.trim()}
              aria-hidden
            >
              {isUser ? (
                <span className={styles.messageAvatarInitial}>{chatUserInitial}</span>
              ) : (
                <AdvisorIcon size="md" />
              )}
            </span>
          ) : (
            <span className={styles.messageAvatarSpacer} aria-hidden />
          )}
          <div className={styles.messageBody}>
            {showHeader ? (
              <div className={styles.messageMeta}>
                <span className={styles.messageRole}>
                  {isUser ? chatUserDisplayName : t("cx_gpt.assistant_name")}
                </span>
                {msg.modality === "voice" ? (
                  <span className={styles.voiceModalityBadge}>
                    {t("cx_gpt.voice_modality_badge")}
                  </span>
                ) : null}
              </div>
            ) : null}
            {!isUser && msg.isPlainText && String(msg.content || "").trim() ? (
              <div
                className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}
              >
                {msg.content}
              </div>
            ) : !isUser && String(msg.content || "").trim() ? (
              <div
                className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}
                dangerouslySetInnerHTML={{
                  __html: formatGptResponseHtml(msg.content),
                }}
              />
            ) : !isUser ? null : (
              <div
                className={`${styles.messageBubble} ${styles.messageBubbleUser}`}
              >
                {msg.content}
              </div>
            )}
            {!isUser &&
            !msg.streaming &&
            !msg.dashboardPicker &&
            !msg.periodPicker &&
            !msg.kpiMetricPicker &&
            !msg.kpiTeamPicker &&
            String(msg.content || "").trim() ? (
              <div className={styles.messageActions} aria-label={t("cx_gpt.message_actions")}>
                <button
                  type="button"
                  className={`${styles.messageActionBtn} ${
                    activeSession?.messageFeedback?.[String(index)] === "positive"
                      ? styles.messageActionBtnActive
                      : ""
                  }`.trim()}
                  title={t("cx_gpt.feedback_positive")}
                  aria-pressed={
                    activeSession?.messageFeedback?.[String(index)] === "positive"
                  }
                  onClick={() => handleMessageFeedback(index, "positive", msg)}
                >
                  {activeSession?.messageFeedback?.[String(index)] === "positive" ? (
                    <LikeFilled />
                  ) : (
                    <LikeOutlined />
                  )}
                </button>
                <button
                  type="button"
                  className={`${styles.messageActionBtn} ${
                    activeSession?.messageFeedback?.[String(index)] === "negative"
                      ? styles.messageActionBtnActive
                      : ""
                  }`.trim()}
                  title={t("cx_gpt.feedback_negative")}
                  aria-pressed={
                    activeSession?.messageFeedback?.[String(index)] === "negative"
                  }
                  onClick={() => handleMessageFeedback(index, "negative", msg)}
                >
                  {activeSession?.messageFeedback?.[String(index)] === "negative" ? (
                    <DislikeFilled />
                  ) : (
                    <DislikeOutlined />
                  )}
                </button>
                <button
                  type="button"
                  className={styles.messageActionBtn}
                  title={t("cx_gpt.copy_message")}
                  onClick={() => handleCopyMessage(msg)}
                >
                  <CopyOutlined />
                </button>
              </div>
            ) : null}
            {!isUser && Array.isArray(msg.charts) && msg.charts.length > 0 ? (
              <div className={styles.messageCharts}>
                {msg.charts.map((chart, chartIdx) => (
                  <AdvisorChartBlock
                    key={`${chart.title || "chart"}-${chartIdx}`}
                    chart={chart}
                    theme={theme}
                    sparklineOnly={sparklineOnly}
                  />
                ))}
              </div>
            ) : null}
            {!isUser &&
            !isPageActive &&
            !msg.dashboardPicker &&
            !msg.periodPicker &&
            !msg.kpiMetricPicker &&
            !msg.kpiTeamPicker &&
            (followupsPending ||
              msg.guidance ||
              (Array.isArray(msg.suggestedFollowups) &&
                msg.suggestedFollowups.length > 0)) ? (
              <div className={styles.messageGuidance}>
                {followupsPending ? (
                  <div
                    className={styles.followupsLoading}
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <div className={styles.followupsLoadingHeader}>
                      <Spin size="small" />
                      <span>{t("cx_gpt.followups_loading")}</span>
                    </div>
                    <div className={styles.followupsLoadingSkeleton} aria-hidden>
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                ) : (
                  <>
                    {msg.guidance ? (
                      <div
                        className={styles.messageGuidanceText}
                        dangerouslySetInnerHTML={{
                          __html: formatGptResponseHtml(msg.guidance),
                        }}
                      />
                    ) : null}
                    {Array.isArray(msg.suggestedFollowups) &&
                    msg.suggestedFollowups.length ? (
                      <div
                        className={
                          msg.guidance
                            ? styles.guidanceLinks
                            : `${styles.guidanceLinks} ${styles.guidanceLinksOnly}`
                        }
                      >
                        {msg.suggestedFollowups.map((q, i) => (
                          <button
                            key={`${q}-${i}`}
                            type="button"
                            className={styles.guidanceLink}
                            onClick={() => onSuggestedClick(q)}
                            disabled={loading}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
            {!isUser && msg.dashboardPicker ? (
              <DashboardPicker
                picker={msg.dashboardPicker}
                disabled={false}
                onPick={onDashboardPick}
                t={t}
              />
            ) : null}
            {!isUser && msg.periodPicker ? (
              <PeriodPicker
                picker={msg.periodPicker}
                disabled={false}
                onPick={onPeriodPick}
                t={t}
              />
            ) : null}
            {!isUser && msg.kpiTeamPicker ? (
              <KpiTeamPicker
                picker={msg.kpiTeamPicker}
                disabled={false}
                onPick={onKpiTeamPick}
                t={t}
              />
            ) : null}
            {!isUser && msg.kpiMetricPicker ? (
              <KpiMetricPicker
                picker={msg.kpiMetricPicker}
                disabled={false}
                onPick={onKpiMetricPick}
                t={t}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderSuggestedQuestionButtons = (buttonClassName) =>
    suggestedQuestions.map((q, i) => (
      <button
        key={i}
        type="button"
        className={buttonClassName}
        onClick={() => onSuggestedClick(q)}
        disabled={loading}
      >
        {q}
      </button>
    ));

  const renderAdvisorVoiceStatus = () => {
    if (!isPage || !hasAdvisorVoiceAccess) return null;
    if (advisorVoice.connection === ADVISOR_VOICE_CONNECTION.IDLE && !advisorVoice.errorText) {
      return null;
    }
    return (
      <div className={styles.advisorVoiceStatus}>
        {advisorVoice.isVoiceActive ? (
          <Tag color="purple">{t("cx_gpt.voice_mode_badge")}</Tag>
        ) : null}
        {advisorVoice.connection === ADVISOR_VOICE_CONNECTION.READY ? (
          <Tag color="success">{t("cx_gpt.voice_live")}</Tag>
        ) : advisorVoice.connection === ADVISOR_VOICE_CONNECTION.CONNECTING ? (
          <Tag color="processing">{t("cx_gpt.voice_connecting")}</Tag>
        ) : advisorVoice.connection === ADVISOR_VOICE_CONNECTION.ERROR ? (
          <Tag color="error">{t("cx_gpt.voice_error")}</Tag>
        ) : null}
        {advisorVoice.micActive ? (
          <span className={styles.advisorVoiceListening}>{t("cx_gpt.voice_listening")}</span>
        ) : null}
        {advisorVoice.isSpeaking ? (
          <span className={styles.advisorVoiceSpeaking}>{t("cx_gpt.voice_speaking")}</span>
        ) : null}
        {advisorVoice.connection !== ADVISOR_VOICE_CONNECTION.IDLE ? (
          <Button type="link" size="small" onClick={advisorVoice.disconnect}>
            {t("cx_gpt.voice_end_session")}
          </Button>
        ) : null}
        {advisorVoice.errorText ? (
          <span className={styles.advisorVoiceError}>{advisorVoice.errorText}</span>
        ) : null}
      </div>
    );
  };

  const voiceCanvasState = useMemo(() => {
    if (advisorVoice.connection === ADVISOR_VOICE_CONNECTION.CONNECTING) {
      return "connecting";
    }
    if (advisorVoice.connection === ADVISOR_VOICE_CONNECTION.ERROR) {
      return "error";
    }
    if (advisorVoice.isSpeaking) {
      return "speaking";
    }
    if (advisorVoice.micActive) {
      return "listening";
    }
    if (advisorVoice.isConnected) {
      return "ready";
    }
    return "idle";
  }, [
    advisorVoice.connection,
    advisorVoice.isConnected,
    advisorVoice.isSpeaking,
    advisorVoice.micActive,
  ]);

  const voiceCanvasStatusText = useMemo(() => {
    if (voiceCanvasState === "connecting") return t("cx_gpt.voice_canvas_connecting");
    if (voiceCanvasState === "error") return t("cx_gpt.voice_error");
    if (voiceCanvasState === "speaking") return t("cx_gpt.voice_canvas_speaking");
    if (voiceCanvasState === "listening") return t("cx_gpt.voice_canvas_listening");
    if (voiceCanvasState === "ready") return t("cx_gpt.voice_canvas_ready");
    return "";
  }, [t, voiceCanvasState]);

  const voiceCanvasHintText = useMemo(() => {
    if (voiceCanvasState === "listening") return t("cx_gpt.voice_canvas_listen_hint");
    if (voiceCanvasState === "ready") return t("cx_gpt.voice_canvas_tap_mic");
    if (voiceCanvasState === "connecting") return t("cx_gpt.voice_canvas_connect_hint");
    if (voiceCanvasState === "speaking") return "";
    if (voiceCanvasState === "error") return advisorVoice.errorText || "";
    return "";
  }, [advisorVoice.errorText, t, voiceCanvasState]);

  const renderVoiceWaveform = () => (
    <div
      className={styles.voiceWaveform}
      data-active={
        advisorVoice.micActive || advisorVoice.isSpeaking ? "true" : "false"
      }
      style={{ "--voice-level": advisorVoice.audioLevel }}
      aria-hidden
    >
      {Array.from({ length: 12 }, (_, index) => (
        <span
          key={index}
          className={styles.voiceWaveBar}
          style={{ animationDelay: `${index * 0.07}s` }}
        />
      ))}
    </div>
  );

  const renderVoiceCanvas = () => {
    if (!showVoiceCanvas) return null;
    const hasTranscript = voiceSessionMessages.length > 0;
    return (
      <div className={styles.voiceCanvas} data-state={voiceCanvasState}>
        <div className={styles.voiceOrbWrap}>
          <span className={styles.voiceOrbRing} aria-hidden />
          <span className={styles.voiceOrbRingSecondary} aria-hidden />
          <div className={styles.voiceOrbCore}>
            {renderVoiceWaveform()}
          </div>
        </div>
        <p className={styles.voiceCanvasStatus}>{voiceCanvasStatusText}</p>
        {voiceCanvasHintText && !hasTranscript ? (
          <p className={styles.voiceCanvasHint}>{voiceCanvasHintText}</p>
        ) : null}
        {hasTranscript ? (
          <div className={styles.voiceTranscriptThread}>
            {voiceSessionMessages.map((msg, index) => {
              const plain =
                typeof msg.content === "string"
                  ? msg.content.split("\n\n")[0].trim()
                  : "";
              if (!plain) return null;
              const isUser = msg.role === "user";
              return (
                <div
                  key={`${msg.ts || index}-${msg.role}`}
                  className={
                    isUser ? styles.voiceLiveLineUser : styles.voiceLiveLineAssistant
                  }
                >
                  <span className={styles.voiceLiveLabel}>
                    {isUser ? t("cx_gpt.you") : t("cx_gpt.assistant_name")}
                  </span>
                  <span>{plain}</span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderComposerSection = ({ hero = false } = {}) => (
    <div
      className={`${styles.composer} ${hero ? styles.composerHero : ""} ${
        !hero ? styles.composerDock : ""
      }`.trim()}
    >
      {renderAdvisorVoiceStatus()}
      <div
        className={`${styles.geminiPromptBar} ${
          hero ? styles.geminiPromptBarHero : ""
        }`.trim()}
      >
        {isPage && hasAdvisorVoiceAccess ? (
          <Button
            type="text"
            shape="circle"
            icon={
              advisorVoice.micActive ? <AudioMutedOutlined /> : <AudioOutlined />
            }
            className={`${styles.geminiMicBtn} ${
              advisorVoice.micActive ? styles.geminiMicBtnActive : ""
            }`.trim()}
            onClick={handleAdvisorVoiceMicClick}
            disabled={loading || advisorVoice.isConnecting}
            aria-label={
              advisorVoice.micActive
                ? t("cx_gpt.voice_mic_stop")
                : t("cx_gpt.voice_mic_start")
            }
            title={
              advisorVoice.micActive
                ? t("cx_gpt.voice_mic_stop")
                : t("cx_gpt.voice_mic_start")
            }
          />
        ) : null}
        <Input.TextArea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => pinNavbarIntoView()}
          onKeyDown={handleKeyDown}
          placeholder={
            hero
              ? t("cx_gpt.page_input_placeholder")
              : t("cx_gpt.placeholder_cursor")
          }
          variant="borderless"
          className={styles.geminiPromptInput}
          autoSize={{ minRows: 1, maxRows: hero ? 4 : 6 }}
          disabled={loading}
        />
        <Button
          type="text"
          shape="circle"
          icon={<SendOutlined />}
          className={styles.geminiSendBtn}
          onClick={handleComposerSend}
          loading={loading}
          disabled={loading || !inputValue.trim() || contextUsage.isFull}
          aria-label={t("cx_gpt.send")}
        />
      </div>
      <div
        className={`${styles.composerFooter} ${
          hero ? styles.composerFooterHero : ""
        }`.trim()}
      >
        {!hero ? (
          <p className={styles.composerDisclaimer}>{disclaimerFooter}</p>
        ) : null}
        {contextUsage.percent > 0 && !loading ? (
          <ContextUsageIndicator usage={contextUsage} />
        ) : null}
      </div>
    </div>
  );

  const onMessageListClick = (e) => {
    const btn = e.target.closest(".cxGptCodeCopy");
    if (!btn) return;
    const wrap = btn.closest(".cxGptCodeBlock");
    const codeEl = wrap?.querySelector("code");
    const text = codeEl?.textContent || "";
    if (text) {
      navigator.clipboard.writeText(text).then(
        () => message.success(t("cx_gpt.copied")),
        () => message.error(t("cx_gpt.copy_failed"))
      );
    }
  };

  return (
    <div
      className={`${styles.panel} ${isPage ? styles.pageVariant : ""} ${
        styles[theme] || ""
      } ${isPage ? styles.pageShell : ""} ${
        isPage && !historyDrawer ? styles.layoutWithRail : ""
      } ${historyDrawer ? styles.pageDrawer : ""}`.trim()}
    >
      {isPage && !historyDrawer
        ? renderChatSessionRail({
            collapsed: pageRailCollapsed,
            onToggleCollapse: togglePageRailCollapsed,
          })
        : null}

      <div className={styles.pageMain}>
        {(!isPage && panelHistoryOpen) || (historyDrawer && panelHistoryOpen) ? (
          <>
            <button
              type="button"
              className={styles.historyDrawerBackdrop}
              onClick={closePanelHistory}
              aria-label={t("cx_gpt.history_close")}
            />
            {renderChatSessionRail({
              className: styles.chatSessionRailDrawer,
              onToggleCollapse: closePanelHistory,
            })}
          </>
        ) : null}
        {sessionsLoading ? (
          <div className={styles.sessionsLoading}>
            <Spin size="small" />
          </div>
        ) : null}
        <div className={`${styles.header} ${styles.advisorToolbar}`}>
          <div className={styles.headerBrand}>
            <span className={styles.headerLogoMark} aria-hidden>
              <AdvisorIcon size="md" />
            </span>
            <div className={styles.headerTitles}>
              <span className={styles.title}>{t("cx_gpt.panel_brand")}</span>
              {hasCxGptAccess && advisorPlanName ? (
                <div className={styles.headerPlanRow}>
                  <span className={styles.headerPlanBadge}>
                    {advisorPlanName}
                    {advisorIsPro ? "" : ` · ${t("cx_gpt.plan_basic_hint")}`}
                  </span>
                  {advisorTrial?.active && advisorTrial.days_left != null ? (
                    <span className={styles.headerTrialChip}>
                      {t("cx_gpt.plan_trial_days_left", { count: advisorTrial.days_left })}
                    </span>
                  ) : null}
                  {trialEnded ? (
                    <span className={styles.headerTrialChip}>{t("cx_gpt.plan_trial_ended")}</span>
                  ) : null}
                  {!advisorIsPro ? (
                    <Link
                      to="/settings/plan"
                      className={styles.headerUpgradeChip}
                    >
                      {t("cx_gpt.upgrade_to_pro")}
                    </Link>
                  ) : null}
                  {trialEnded ? (
                    <a
                      href="https://pivony.com/book-a-demo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.headerUpgradeChip}
                    >
                      {t("cx_gpt.talk_to_sales")}
                    </a>
                  ) : null}
                </div>
              ) : null}
              {advisorPanelTitle ? (
                <span className={styles.headerSessionTitle}>{advisorPanelTitle}</span>
              ) : null}
            </div>
          </div>
          <div className={styles.actions}>
            {historyDrawer ? (
              <>
                <Button
                  type="text"
                  size="small"
                  className={styles.headerPrimaryAction}
                  icon={<PlusOutlined />}
                  onClick={() => {
                    handleNewChat();
                    closePanelHistory();
                  }}
                  title={t("cx_gpt.new_chat_shortcut")}
                  aria-label={t("cx_gpt.new_chat_btn")}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<HistoryOutlined />}
                  onClick={togglePanelHistory}
                  title={t("cx_gpt.history")}
                  aria-label={t("cx_gpt.history")}
                />
                <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  menu={{
                    items: [
                      {
                        key: "clear",
                        icon: <ClearOutlined />,
                        label: t("cx_gpt.clear"),
                        onClick: handleClearCurrent,
                      },
                      {
                        key: "export",
                        icon: <DownloadOutlined />,
                        label: t("cx_gpt.export_conversation", "Export"),
                        disabled: messages.length === 0,
                        onClick: () => setExportChatOpen(true),
                      },
                    ],
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<EllipsisOutlined />}
                    title={t("cx_gpt.more")}
                    aria-label={t("cx_gpt.more")}
                  />
                </Dropdown>
              </>
            ) : !isPage ? (
              <>
                <Button
                  type="text"
                  size="small"
                  className={styles.headerPrimaryAction}
                  icon={<PlusOutlined />}
                  onClick={handleNewChat}
                  title={t("cx_gpt.new_chat_shortcut")}
                >
                  {t("cx_gpt.new_chat_btn")}
                </Button>
                <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  menu={{
                    items: [
                      {
                        key: "history",
                        icon: <HistoryOutlined />,
                        label: t("cx_gpt.history"),
                        onClick: togglePanelHistory,
                      },
                      {
                        key: "clear",
                        icon: <ClearOutlined />,
                        label: t("cx_gpt.clear"),
                        onClick: handleClearCurrent,
                      },
                      {
                        key: "export",
                        icon: <DownloadOutlined />,
                        label: t("cx_gpt.export_conversation", "Export"),
                        disabled: messages.length === 0,
                        onClick: () => setExportChatOpen(true),
                      },
                      ...(onOpenFullPage
                        ? [
                            {
                              key: "fullpage",
                              icon: <FullscreenOutlined />,
                              label: t("cx_gpt.open_full_page"),
                              onClick: onOpenFullPage,
                            },
                          ]
                        : []),
                      ...(onToggleWide
                        ? [
                            {
                              key: "wide",
                              icon: <ExpandOutlined />,
                              label: t("cx_gpt.toggle_width"),
                              onClick: onToggleWide,
                            },
                          ]
                        : []),
                    ],
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<EllipsisOutlined />}
                    title={t("cx_gpt.more")}
                    aria-label={t("cx_gpt.more")}
                  />
                </Dropdown>
              </>
            ) : (
              <Button
                type="text"
                size="small"
                className={styles.headerExportAction}
                icon={<DownloadOutlined />}
                onClick={() => setExportChatOpen(true)}
                disabled={messages.length === 0}
                title={`Export conversation (${exportShortcutHint})`}
                aria-label={`Export conversation (${exportShortcutHint})`}
              />
            )}
            {onDockToPanel && isPage ? (
              <Button
                type="text"
                size="small"
                icon={<ColumnWidthOutlined />}
                onClick={onDockToPanel}
                title={t("cx_gpt.dock_to_panel")}
              />
            ) : null}
            {onClose ? (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={onClose}
                title={isPage ? t("cx_gpt.page_back") : t("cx_gpt.close")}
              />
            ) : null}
          </div>
        </div>

        {!hasCxGptAccess ? (
          <div className={styles.planUpsellBanner}>
            <span className={styles.planUpsellText}>{t("cx_gpt.plan_upsell_banner")}</span>
            <Link to="/settings/plan" className={styles.planUpsellCta}>
              {t("cx_gpt.plan_view_plans")}
            </Link>
          </div>
        ) : null}

        {!isPage ? (
          <div className={styles.contextBar}>
            <button
              type="button"
              className={styles.contextToggle}
              onClick={() => setContextExpanded((v) => !v)}
              aria-expanded={contextExpanded}
            >
              <span className={styles.contextToggleLabel}>
                {contextExpanded ? t("cx_gpt.context_hide") : t("cx_gpt.context_show")}
              </span>
              {contextExpanded ? <UpOutlined /> : <DownOutlined />}
            </button>
            {contextExpanded ? (
              <div className={styles.contextSummary}>
                {context.hasDashboard ? (
                  <>
                    <span className={styles.contextChip}>
                      {context.dashboardName}
                    </span>
                    <span className={styles.contextChip}>
                      {context.subPage || t("cx_gpt.context_overview")}
                    </span>
                    <span className={styles.contextChip}>
                      {formatContextRange(
                        context.since,
                        context.until,
                        context.selectedDaysRange
                      )}
                    </span>
                    {context.filterLabels.map((label) => (
                      <span key={label} className={styles.contextChip}>
                        {label}
                      </span>
                    ))}
                  </>
                ) : (
                  <span className={styles.contextChipMuted}>
                    {t("cx_gpt.context_general_assistant")}
                  </span>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {isPageHero ? (
          <div className={styles.pageHeroGemini}>
            <div className={styles.pageHeroGeminiInner}>
              {showVoiceCanvas ? (
                renderVoiceCanvas()
              ) : (
                <h1 className={styles.pageHeroGreeting}>{heroGreeting}</h1>
              )}
              <div className={styles.pageHeroComposer}>
                {renderComposerSection({ hero: true })}
              </div>
              {!suggestionsDismissed && hasSuggestedQuestions && !showVoiceCanvas
                ? renderHeroStarterChips()
                : null}
              {!showVoiceCanvas ? (
                <button
                  type="button"
                  className={styles.pageHeroExploreToggle}
                  onClick={() => setShowCapabilities((v) => !v)}
                  aria-expanded={showCapabilities}
                >
                  {showCapabilities
                    ? t("cx_gpt.page_explore_hide")
                    : t("cx_gpt.page_explore_toggle")}
                </button>
              ) : null}
              {!showVoiceCanvas && showCapabilities ? (
                <div className={styles.capabilityGridCompact}>
                  {capabilityCards.map((card) => {
                    const Icon = card.icon;
                    return (
                      <button
                        key={card.key}
                        type="button"
                        className={styles.capabilityCardCompact}
                        onClick={() => onSuggestedClick(card.desc)}
                        disabled={loading}
                      >
                        <span className={styles.capabilityIconWrapCompact}>
                          <Icon className={styles.capabilityIcon} />
                        </span>
                        <span className={styles.capabilityTitleCompact}>{card.title}</span>
                        <span className={styles.capabilityDescCompact}>{card.desc}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div
              className={`${styles.conversationArea} ${
                isPageActive ? styles.pageConversationArea : styles.chatConversationArea
              }`.trim()}
            >
              <div
                ref={messageListRef}
                className={`${styles.messageList} ${styles.chatMessageList} ${
                  isPageActive ? styles.pageMessageList : ""
                }`.trim()}
                onClick={onMessageListClick}
                role="presentation"
              >
                {sessionsLoading && messages.length === 0 && (
                  <div className={styles.placeholder}>
                    <Spin size="small" style={{ marginRight: 8 }} />
                    {t("cx_gpt.loading_last_conversation")}
                  </div>
                )}
                {showEmptySuggestions && !sessionsLoading && (
                  <div className={styles.suggestedEmptyState}>
                    <div className={styles.suggestedHeader}>
                      <span className={styles.suggestedLabel}>
                        {t("cx_gpt.suggested_questions_title")}
                      </span>
                      <button
                        type="button"
                        className={styles.suggestedIconBtn}
                        onClick={dismissSuggestions}
                        aria-label={t("cx_gpt.suggested_hide")}
                        title={t("cx_gpt.suggested_hide")}
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                    {renderSuggestedQuestionButtons(styles.suggestedBtn)}
                  </div>
                )}
                {messages.length === 0 &&
                  !loading &&
                  !sessionsLoading &&
                  !showEmptySuggestions && (
                    <div className={styles.placeholder}>{t("cx_gpt.empty")}</div>
                  )}
                {groupedMessages.map((msg, index) => renderMessage(msg, index))}
                {loading && !streamContentStarted && (
                  <div className={styles.messageAssistant}>
                    <div className={styles.messageRow}>
                      <span className={styles.messageAvatar} aria-hidden>
                        <AdvisorIcon size="md" />
                      </span>
                      <div className={styles.messageBody}>
                        <div className={styles.messageMeta}>
                          <span className={styles.messageRole}>
                            {t("cx_gpt.assistant_name")}
                          </span>
                        </div>
                        <div
                          className={`${styles.messageBubble} ${styles.messageBubbleAssistant} ${styles.typingBubble}`.trim()}
                          aria-live="polite"
                          aria-busy="true"
                        >
                          <span className={styles.typingContent}>
                            <span className={styles.typingDots} aria-hidden>
                              <span />
                              <span />
                              <span />
                            </span>
                            <span>{t("cx_gpt.thinking")}</span>
                          </span>
                          {thinkingToolLabel ? (
                            <div className={styles.thinkingStreamPanel}>
                              <div className={styles.thinkingStreamAction}>
                                {thinkingToolLabel}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {ratingCard && !loading && messages.length > 0 ? (
                  <AdvisorRatingCard
                    stars={ratingCard.stars}
                    comment={ratingCard.comment}
                    status={ratingCard.status}
                    onStarsChange={setRatingStars}
                    onCommentChange={setRatingComment}
                    onSubmit={submitRating}
                    onSkip={skipRating}
                  />
                ) : null}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div
              className={`${isPageActive ? styles.pageComposerWrap : styles.panelComposerWrap}`}
            >
              {renderComposerSection()}
            </div>
          </>
        )}
      </div>
      <ExportChatModal
        open={exportChatOpen}
        onClose={() => setExportChatOpen(false)}
        conversationId={activeSession?.id || copilotState.activeSessionId}
        title={displaySessionTitle(activeSession?.title || t("cx_gpt.new_chat"))}
        messages={messages}
      />
    </div>
  );
};

export default GlobalCopilotPanel;
